import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import PaymentBadges from "../components/PaymentBadges";
import { trackBeginCheckout, trackPurchase } from "@/lib/analytics";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shipping, setShipping] = useState({
    email: "",
    name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "India",
  });

  useEffect(() => {
    loadRazorpay();
  }, []);

  useEffect(() => {
    if (user) {
      setShipping((prev) => ({
        ...prev,
        email: prev.email || user.email || "",
        name: prev.name || user.name || "",
      }));
    }
  }, [user]);

  if (items.length === 0) {
    return (
      <div className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="label-eyebrow mb-4">Checkout</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tighter">
            Your bag is empty
          </h1>
          <p className="mt-6 text-[#666]">Add a pair to begin.</p>
          <button
            onClick={() => nav("/shop")}
            className="mt-8 bg-[#111] text-white px-8 py-4 text-sm font-medium hover:bg-[#333]"
          >
            Shop the Collection
          </button>
        </div>
      </div>
    );
  }

  const validate = () => {
    const required = [
      ["name", "Full name"],
      ["phone", "Phone number"],
      ["email", "Email"],
      ["address", "Address"],
      ["city", "City"],
      ["state", "State"],
      ["postal_code", "Pincode"],
      ["country", "Country"],
    ];
    for (const [f, label] of required) {
      if (!shipping[f] || !shipping[f].trim()) {
        toast.error(`Please fill ${label}`);
        return false;
      }
    }
    const phoneClean = shipping.phone.replace(/\D/g, "");
    if (phoneClean.length < 10) {
      toast.error("Please enter a valid phone number");
      return false;
    }
    if (!/^\d{6}$/.test(shipping.postal_code.replace(/\s/g, ""))) {
      toast.error("Please enter a valid 6-digit Indian pincode");
      return false;
    }
    return true;
  };

  const openPaymentWithMethod = async (preferredMethod) => {
    if (!validate()) return;

    setLoading(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Could not load payment gateway. Please try again.");
        setLoading(false);
        return;
      }

      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          size: i.size,
          color: i.color || null,
        })),
        guest: shipping,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/session", payload);
      trackBeginCheckout(items, subtotal);

      // Pre-select payment method in Razorpay based on which badge/button was clicked
      const upiApps = ["gpay", "phonepe", "paytm"];
      const methodKey = (preferredMethod || "").toLowerCase().replace(/\s+/g, "");
      let displayConfig = undefined;
      if (methodKey === "upi" || upiApps.includes(methodKey)) {
        displayConfig = {
          blocks: { upi: { name: "Pay via UPI", instruments: [{ method: "upi" }] } },
          sequence: ["block.upi"],
          preferences: { show_default_blocks: true },
        };
      } else if (["visa", "mastercard", "rupay"].includes(methodKey)) {
        displayConfig = {
          blocks: { card: { name: "Pay by Card", instruments: [{ method: "card" }] } },
          sequence: ["block.card"],
          preferences: { show_default_blocks: true },
        };
      } else if (methodKey === "netbanking") {
        displayConfig = {
          blocks: { nb: { name: "Net Banking", instruments: [{ method: "netbanking" }] } },
          sequence: ["block.nb"],
          preferences: { show_default_blocks: true },
        };
      }

      const options = {
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        name: "K-Walk",
        description: "Handcrafted footwear",
        order_id: data.razorpay_order_id,
        prefill: {
          name: shipping.name,
          email: shipping.email,
          contact: shipping.phone,
        },
        notes: {
          address: `${shipping.address}, ${shipping.city}, ${shipping.state} ${shipping.postal_code}`,
        },
        theme: { color: "#111111" },
        handler: async (resp) => {
          try {
            await api.post("/checkout/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              order_id: data.order_id,
            });
            trackPurchase({
              transaction_id: data.order_id,
              value: subtotal,
              items,
            });
            clear();
            nav(`/checkout/success?order_id=${data.order_id}`);
          } catch (err) {
            toast.error(
              formatApiError(err.response?.data?.detail) || "Payment verification failed",
            );
            setLoading(false);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };
      if (displayConfig) options.config = { display: displayConfig };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    openPaymentWithMethod(null);
  };

  const upd = (k, v) => setShipping({ ...shipping, [k]: v });

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="label-eyebrow mb-4">Checkout</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mb-12">
          Almost yours.
        </h1>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="mb-10">
              <div className="label-eyebrow mb-6">Contact & Shipping Address</div>
              {user && (
                <p className="text-xs text-[#666] mb-6">
                  Signed in as {user.email}. Please confirm your delivery details below.
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { k: "name", label: "Full name", full: true },
                  { k: "phone", label: "Phone (10-digit)", type: "tel" },
                  { k: "email", label: "Email", type: "email" },
                  { k: "address", label: "Address (house no., street, area)", full: true },
                  { k: "city", label: "City" },
                  { k: "state", label: "State" },
                  { k: "postal_code", label: "Pincode (6-digit)" },
                  { k: "country", label: "Country" },
                ].map((f) => (
                  <label key={f.k} className={`block ${f.full ? "col-span-2" : ""}`}>
                    <span className="block label-eyebrow mb-2">{f.label}</span>
                    <input
                      type={f.type || "text"}
                      value={shipping[f.k]}
                      onChange={(e) => upd(f.k, e.target.value)}
                      className="w-full bg-transparent border-b border-[#e5e5e5] py-3 text-base focus:outline-none focus:border-[#111] transition-colors"
                      data-testid={`checkout-${f.k}`}
                    />
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#111] text-white px-8 py-5 text-sm font-medium hover:bg-[#333] transition-colors disabled:opacity-60"
              data-testid="pay-button"
            >
              {loading ? "Opening payment…" : `Pay ₹${subtotal.toFixed(2)} securely`}
            </button>
            <p className="text-xs text-[#666] mt-4">
              Secure checkout powered by Razorpay. Pay with UPI, cards, or net banking.
            </p>
            <div className="mt-6">
              <PaymentBadges onSelect={openPaymentWithMethod} />
            </div>
          </div>

          <div className="lg:border-l lg:border-[#e5e5e5] lg:pl-16">
            <div className="label-eyebrow mb-6">Order Summary</div>
            <ul className="space-y-6">
              {items.map((it) => (
                <li key={`${it.product_id}-${it.size}-${it.color || ""}`} className="flex gap-4">
                  <div className="w-20 h-24 bg-[#f5f5f5] flex-shrink-0 overflow-hidden">
                    <img
                      src={it.image_url}
                      alt={it.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 text-sm">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-[#666] text-xs mt-1">
                      Size {it.size}{it.color ? ` · ${it.color}` : ""} · Qty {it.quantity}
                    </div>
                    <div className="mt-1">₹{(it.price * it.quantity).toFixed(2)}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-10 pt-6 border-t border-[#e5e5e5] space-y-2 text-sm">
              <div className="flex justify-between text-[#666]">
                <span>Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[#666]">
                <span>Shipping</span>
                <span>Free across India</span>
              </div>
              <div className="flex justify-between font-display text-lg font-bold mt-4">
                <span>Total</span>
                <span data-testid="order-total">₹{subtotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
